/* NAMAI Kaira theme — interactions (no framework, no build step). */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mq = (q) => window.matchMedia(q).matches;
  const config = window.NamaiKaira || { routes: {} };

  /* ---------- Screen-reader announcements ---------- */
  const live = () => $('[data-live-region]');
  let liveTimer;
  function announce(message) {
    const el = live();
    if (!el) return;
    clearTimeout(liveTimer);
    el.textContent = '';
    liveTimer = setTimeout(() => { el.textContent = message; }, 60);
  }

  /* ---------- In-page navigation ---------- */
  function focusSection(el) {
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }
  function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return false;
    el.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    focusSection(el);
    if (history.replaceState) history.replaceState(null, '', `#${id}`);
    return true;
  }
  /** Links may carry data-gallery (select a product image) and data-accordion (open an accordion). */
  function go(hash, opts = {}) {
    const id = hash.replace(/^#/, '');
    if (opts.gallery != null) Gallery.select(Number(opts.gallery));
    if (opts.accordion) {
      const acc = document.getElementById(`acc-${opts.accordion}`);
      if (acc) {
        const trigger = $('[data-accordion-trigger]', acc);
        if (trigger) Accordion.open(trigger);
        requestAnimationFrame(() => scrollToId(acc.id));
        return true;
      }
    }
    return scrollToId(id);
  }
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#"]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    const url = new URL(a.href, location.href);
    if (url.pathname !== location.pathname || !url.hash || url.hash === '#') return;
    const id = url.hash.slice(1);
    if (!document.getElementById(id) && !a.dataset.accordion) return;
    e.preventDefault();
    if (a.hasAttribute('data-close-dialog-link')) Dialogs.closeAll();
    requestAnimationFrame(() => go(url.hash, { gallery: a.dataset.gallery, accordion: a.dataset.accordion }));
  });
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-close-dialog-link]');
    if (a && !a.getAttribute('href').includes('#')) Dialogs.closeAll();
  });

  /* ---------- Dialogs (drawers, modals, lightbox) ---------- */
  const Dialogs = (() => {
    const stack = [];
    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let savedPadding = '';

    function lockScroll(on) {
      const body = document.body;
      if (on) {
        const sb = window.innerWidth - document.documentElement.clientWidth;
        savedPadding = body.style.paddingRight;
        if (sb > 0) body.style.paddingRight = `${sb}px`;
        document.documentElement.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = '';
        body.style.overflow = '';
        body.style.paddingRight = savedPadding;
      }
    }
    function setPageInert(on) {
      const page = $('#PageContainer');
      if (page) page.inert = on;
    }
    function trap(e) {
      const top = stack[stack.length - 1];
      if (!top) return;
      if (e.key === 'Escape') { e.preventDefault(); close(top.el); return; }
      if (e.key !== 'Tab') return;
      const panel = $('[data-dialog-panel]', top.el);
      const items = $$(FOCUSABLE, panel).filter((n) => n.getClientRects().length > 0);
      if (!items.length) { e.preventDefault(); panel.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function open(id, opener) {
      const el = $(`[data-dialog="${id}"]`);
      if (!el || stack.some((s) => s.el === el)) return;
      // a dialog opened from inside another drawer replaces it (e.g. menu → cart)
      if (opener && opener.closest('[data-dialog]')) close(opener.closest('[data-dialog]'), { restoreFocus: false });
      const restore = opener && !opener.closest('[data-dialog]') ? opener : document.activeElement;
      el.hidden = false;
      if (!stack.length) { lockScroll(true); setPageInert(true); document.addEventListener('keydown', trap); }
      stack.push({ el, restore });
      requestAnimationFrame(() => requestAnimationFrame(() => {
        el.dataset.state = 'open';
        const panel = $('[data-dialog-panel]', el);
        const first = $$(FOCUSABLE, panel).find((n) => n.getClientRects().length > 0);
        (first || panel).focus({ preventScroll: true });
      }));
      el.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    }

    function close(el, { restoreFocus = true } = {}) {
      const i = stack.findIndex((s) => s.el === el);
      if (i < 0) return;
      const [{ restore }] = stack.splice(i, 1);
      el.dataset.state = 'closed';
      const done = () => { if (el.dataset.state === 'closed') el.hidden = true; };
      reduceMotion() ? done() : setTimeout(done, 380);
      if (!stack.length) { lockScroll(false); setPageInert(false); document.removeEventListener('keydown', trap); }
      if (restoreFocus && restore && document.contains(restore)) restore.focus({ preventScroll: true });
      el.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
    }

    function closeAll() { [...stack].reverse().forEach((s) => close(s.el, { restoreFocus: false })); }

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open-dialog]');
      if (opener) { e.preventDefault(); open(opener.dataset.openDialog, opener); return; }
      const closer = e.target.closest('[data-close-dialog]');
      if (closer) close(closer.closest('[data-dialog]'));
    });

    return { open, close, closeAll, isOpen: (id) => stack.some((s) => s.el.dataset.dialog === id) };
  })();

  /* ---------- Accordions (single-open groups; mobile menu) ---------- */
  const Accordion = (() => {
    function set(trigger, open) {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      trigger.setAttribute('aria-expanded', String(open));
      if (!panel) return;
      panel.dataset.open = String(open);
      panel.inert = !open;
    }
    function open(trigger) {
      const group = trigger.closest('[data-accordion-group]');
      if (group && group.dataset.single === 'true') {
        $$('[data-accordion-trigger]', group).forEach((t) => { if (t !== trigger) set(t, false); });
      }
      set(trigger, true);
    }
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-accordion-trigger]');
      if (!t) return;
      t.getAttribute('aria-expanded') === 'true' ? set(t, false) : open(t);
    });
    return { open, set };
  })();

  // Mobile menu: category shortcuts open the drawer with that section expanded
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-open-dialog="mobile-nav"]');
    if (!b) return;
    const idx = b.dataset.expand;
    $$('[data-mnav-index]').forEach((t) => Accordion.set(t, idx != null && t.dataset.mnavIndex === idx));
  });

  /* ---------- Footer groups (mobile) ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-footer-toggle]');
    if (!t) return;
    const open = t.getAttribute('aria-expanded') !== 'true';
    t.setAttribute('aria-expanded', String(open));
    document.getElementById(t.getAttribute('aria-controls'))?.classList.toggle('is-open', open);
  });

  /* ---------- Popovers (account, region) ---------- */
  function initPopovers() {
    $$('[data-popover]').forEach((wrap) => {
      const btn = $('[data-popover-toggle]', wrap);
      const panel = $('[data-popover-panel]', wrap);
      const setOpen = (open) => { btn.setAttribute('aria-expanded', String(open)); panel.hidden = !open; };
      btn.addEventListener('click', () => setOpen(panel.hidden));
      document.addEventListener('pointerdown', (e) => { if (!panel.hidden && !wrap.contains(e.target)) setOpen(false); });
      wrap.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) { setOpen(false); btn.focus(); } });
    });
  }

  /* ---------- Localization auto-submit ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-autosubmit]')) e.target.form.submit();
  });

  /* ---------- Announcement bar ---------- */
  function initAnnouncement() {
    const bar = $('[data-announcement]');
    if (!bar) return;
    const items = $$('[data-announcement-item]', bar);
    if (items.length < 2) return;
    let i = 0;
    let paused = false;
    const interval = (Number(bar.closest('[data-interval]')?.dataset.interval) || 5000);
    ['mouseenter', 'focusin'].forEach((ev) => bar.addEventListener(ev, () => { paused = true; }));
    ['mouseleave', 'focusout'].forEach((ev) => bar.addEventListener(ev, () => { paused = false; }));
    setInterval(() => {
      if (paused) return;
      const prev = items[i];
      i = (i + 1) % items.length;
      prev.classList.replace('opacity-100', 'opacity-0');
      prev.classList.replace('translate-y-0', 'translate-y-2');
      prev.setAttribute('aria-hidden', 'true');
      items[i].classList.replace('opacity-0', 'opacity-100');
      items[i].classList.replace('translate-y-2', 'translate-y-0');
      items[i].removeAttribute('aria-hidden');
    }, interval);
  }

  /* ---------- Mobile header (compact scroll-up reveal) + search toggle ---------- */
  function initMobileHeader() {
    const header = $('[data-mobile-header]');
    if (!header) return;
    let lastY = window.scrollY;
    const toggle = $('[data-mobile-search-toggle]', header);
    const panel = $('#mobile-search');
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) < 6) return;
      const searchOpen = toggle?.getAttribute('aria-expanded') === 'true';
      header.classList.toggle('is-hidden', y > lastY && y > 160 && !searchOpen);
      lastY = y;
    }, { passive: true });
    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close search' : 'Search');
      panel.hidden = !open;
      if (open) $('[data-search-input]', panel)?.focus();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle?.getAttribute('aria-expanded') === 'true' && !$('[data-search].is-open', panel)) {
        toggle.click(); toggle.focus();
      }
    });
  }

  /* ---------- Mega menu ---------- */
  function initMega() {
    const root = $('[data-mega]');
    if (!root) return;
    const triggers = $$('[data-mega-trigger]', root);
    let active = null;
    let openTimer;
    let closeTimer;
    const panelFor = (t) => document.getElementById(t.getAttribute('aria-controls'));
    const close = () => {
      clearTimeout(openTimer);
      if (!active) return;
      active.setAttribute('aria-expanded', 'false');
      panelFor(active)?.classList.remove('is-open');
      active = null;
    };
    const open = (t) => {
      if (active === t) return;
      DesktopSearch.close();
      if (active) { active.setAttribute('aria-expanded', 'false'); panelFor(active)?.classList.remove('is-open'); }
      active = t;
      t.setAttribute('aria-expanded', 'true');
      panelFor(t)?.classList.add('is-open');
    };
    triggers.forEach((t, i) => {
      t.closest('li').addEventListener('mouseenter', () => {
        clearTimeout(closeTimer); clearTimeout(openTimer);
        active ? open(t) : (openTimer = setTimeout(() => open(t), 90));
      });
      t.addEventListener('click', () => (active === t ? close() : open(t)));
      t.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey && active === t) {
          const first = $('a[href]', panelFor(t));
          if (first) { e.preventDefault(); first.focus(); }
        }
      });
      const panel = panelFor(t);
      panel?.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const links = $$('a[href]', panel);
        if (!e.shiftKey && document.activeElement === links[links.length - 1]) {
          const next = triggers[i + 1];
          close();
          if (next) { e.preventDefault(); next.focus(); }
        } else if (e.shiftKey && document.activeElement === links[0]) {
          e.preventDefault(); t.focus();
        }
      });
    });
    root.addEventListener('mouseleave', () => { clearTimeout(openTimer); closeTimer = setTimeout(close, 140); });
    root.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    document.addEventListener('pointerdown', (e) => { if (active && !root.contains(e.target)) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && active) { const t = active; close(); t.focus(); } });
  }

  /* ---------- Desktop search panel (search icon in the header) ---------- */
  const DesktopSearch = (() => {
    let toggle;
    let panel;
    const close = () => {
      if (!toggle || toggle.getAttribute('aria-expanded') !== 'true') return;
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    };
    function init() {
      toggle = $('[data-desktop-search-toggle]');
      panel = $('#desktop-search');
      if (!toggle || !panel) return;
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        if (!open) { close(); return; }
        $$('[data-mega-trigger][aria-expanded="true"]').forEach((t) => t.click());
        toggle.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        $('[data-search-input]', panel)?.focus();
      });
      document.addEventListener('pointerdown', (e) => { if (!panel.contains(e.target) && !toggle.contains(e.target)) close(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !panel.hidden && !$('[data-search].is-open', panel)) { close(); toggle.focus(); }
      });
    }
    return { init, close };
  })();

  /* ---------- Predictive search ---------- */
  function initSearch() {
    $$('[data-search]').forEach((wrap) => {
      const input = $('[data-search-input]', wrap);
      const clear = $('[data-search-clear]', wrap);
      const list = $('[data-search-list]', wrap);
      const results = $('[data-search-results]', wrap);
      const heading = $('[data-search-heading]', wrap);
      const baseOptions = $$('[data-search-option]', list);
      let activeIndex = -1;
      let controller;

      const options = () => $$('[role="option"]', wrap).filter((o) => !o.hidden);
      const setOpen = (open) => { wrap.classList.toggle('is-open', open); input.setAttribute('aria-expanded', String(open)); if (!open) setActive(-1); };
      const setActive = (i) => {
        const opts = options();
        activeIndex = i;
        opts.forEach((o, n) => o.setAttribute('aria-selected', String(n === i)));
        if (opts[i]) { input.setAttribute('aria-activedescendant', opts[i].id); opts[i].scrollIntoView({ block: 'nearest' }); }
        else input.removeAttribute('aria-activedescendant');
      };

      async function fetchResults(q) {
        controller?.abort();
        if (!q) { results.innerHTML = ''; return; }
        controller = new AbortController();
        try {
          const url = `${config.routes.search || '/search/suggest'}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=4`;
          const res = await fetch(url, { signal: controller.signal });
          const data = await res.json();
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = `<p class="border-t border-line px-5 py-4 text-[14px] text-muted" role="status">Nothing matches “${escapeHtml(q)}”. Try “Kantha” or “Reversible”.</p>`;
            return;
          }
          results.innerHTML = `<ul class="border-t border-line px-2 py-2" role="presentation">${products.map((p, i) => `
            <li id="${input.id}-prod-${i}" role="option" aria-selected="false" class="flex cursor-pointer items-center gap-4 px-3 py-3 aria-selected:bg-soft-gray" data-href="${p.url}">
              <div class="w-14 shrink-0">${p.image ? `<div class="relative overflow-hidden" style="aspect-ratio:3/4;background:var(--soft-gray)"><img src="${p.image}${p.image.includes('?') ? '&' : '?'}width=120" alt="" class="absolute inset-0 h-full w-full object-cover" loading="lazy"></div>` : ''}</div>
              <div class="min-w-0 flex-1"><p class="truncate text-[14px] text-navy">${escapeHtml(p.title)}</p><p class="text-[13px] text-muted">${formatMoney(p.price)}</p></div>
            </li>`).join('')}</ul>
            <a href="/search?q=${encodeURIComponent(q)}&type=product" class="block border-t border-line px-5 py-3 text-[12.5px] font-medium uppercase tracking-[0.14em] text-navy underline underline-offset-4">See all results</a>`;
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = '';
        }
      }

      let debounce;
      input.addEventListener('input', () => {
        const q = input.value.trim();
        clear.classList.toggle('is-visible', !!input.value);
        heading.textContent = q ? 'Suggestions' : 'Popular searches';
        baseOptions.forEach((o) => { o.hidden = !!q && !o.dataset.value.toLowerCase().includes(q.toLowerCase()); });
        heading.hidden = baseOptions.every((o) => o.hidden);
        setOpen(true); setActive(-1);
        clearTimeout(debounce);
        debounce = setTimeout(() => fetchResults(q), 180);
      });
      input.addEventListener('focus', () => setOpen(true));
      input.addEventListener('keydown', (e) => {
        const opts = options();
        if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((activeIndex + 1) % Math.max(opts.length, 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex <= 0 ? opts.length - 1 : activeIndex - 1); }
        else if (e.key === 'Escape') { if (wrap.classList.contains('is-open')) { e.stopPropagation(); setOpen(false); } else { input.value = ''; input.dispatchEvent(new Event('input')); } }
        else if (e.key === 'Enter' && activeIndex >= 0 && opts[activeIndex]) { e.preventDefault(); choose(opts[activeIndex]); }
      });
      const choose = (opt) => {
        if (opt.dataset.href) { location.href = opt.dataset.href; return; }
        input.value = opt.dataset.value;
        input.dispatchEvent(new Event('input'));
        input.focus();
      };
      wrap.addEventListener('mousedown', (e) => { if (e.target.closest('[role="option"]')) e.preventDefault(); });
      wrap.addEventListener('click', (e) => { const o = e.target.closest('[role="option"]'); if (o) choose(o); });
      clear.addEventListener('click', () => { input.value = ''; input.dispatchEvent(new Event('input')); input.focus(); });
      document.addEventListener('pointerdown', (e) => { if (!wrap.contains(e.target)) setOpen(false); });
    });
  }

  /* ---------- Hero carousel (multi-panel, seamless loop) ---------- */
  function initHero() {
    $$('[data-hero]').forEach((hero) => {
      const track = $('[data-hero-track]', hero);
      const slides = $$('[data-hero-slide]', track);
      const n = slides.length;
      if (n < 2) return;
      slides.forEach((s) => {
        const c = s.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.tabIndex = -1;
        $$('img', c).forEach((img) => { img.alt = ''; });
        track.appendChild(c);
      });
      const dots = $$('[data-dot]', hero);
      const toggle = $('[data-hero-toggle]', hero);
      let index = 0;
      let busy = false;
      let userPaused = false;
      let hovered = false;
      let focused = false;
      let timer;
      const perView = () => (mq('(min-width: 1024px)') ? 4 : mq('(min-width: 768px)') ? 2 : 1);
      const render = (animate) => {
        track.style.transitionDuration = animate && !reduceMotion() ? '650ms' : '0ms';
        track.style.transform = `translate3d(-${(index * 100) / perView()}%,0,0)`;
        dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index % n)));
        if (!animate || reduceMotion()) busy = false;
      };
      const next = () => { if (busy) return; busy = true; index += 1; render(true); };
      const prev = () => {
        if (busy) return;
        busy = true;
        if (index > 0) { index -= 1; render(true); return; }
        index = n; render(false); busy = true;
        requestAnimationFrame(() => requestAnimationFrame(() => { index = n - 1; render(true); }));
      };
      track.addEventListener('transitionend', (e) => {
        if (e.target !== track) return;
        busy = false;
        if (index >= n) { index -= n; render(false); }
      });
      const schedule = () => {
        clearInterval(timer);
        const paused = userPaused || hovered || focused || reduceMotion() || document.hidden || hero.dataset.autoplay === 'false';
        if (!paused) timer = setInterval(next, Number(hero.dataset.interval) || 5500);
      };
      hero.addEventListener('mouseenter', () => { hovered = true; schedule(); });
      hero.addEventListener('mouseleave', () => { hovered = false; schedule(); });
      hero.addEventListener('focusin', () => { focused = true; schedule(); });
      hero.addEventListener('focusout', (e) => { if (!hero.contains(e.relatedTarget)) { focused = false; schedule(); } });
      document.addEventListener('visibilitychange', schedule);
      hero.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      });
      slides.forEach((s, i) => s.addEventListener('focus', () => {
        const rel = (i - index + n * 2) % (n * 2);
        if (rel >= perView()) { index = i; render(true); }
      }));
      $('[data-hero-next]', hero)?.addEventListener('click', next);
      $('[data-hero-prev]', hero)?.addEventListener('click', prev);
      dots.forEach((d) => d.addEventListener('click', () => { index = Number(d.dataset.dot); render(true); }));
      toggle?.addEventListener('click', () => {
        userPaused = !userPaused;
        toggle.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
        $('[data-icon-pause]', toggle).hidden = userPaused;
        $('[data-icon-play]', toggle).hidden = !userPaused;
        schedule();
      });
      addSwipe(hero, next, prev);
      window.addEventListener('resize', () => render(false));
      render(false);
      schedule();
    });
  }

  function addSwipe(el, onLeft, onRight, threshold = 40) {
    let start = null;
    el.addEventListener('touchstart', (e) => { const t = e.touches[0]; start = { x: t.clientX, y: t.clientY }; }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (!start) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      start = null;
      if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      dx < 0 ? onLeft() : onRight();
    }, { passive: true });
  }

  /* ---------- Snap scrollers (explore, benefits, reviews, looks) ---------- */
  function initScrollers() {
    $$('[data-scroller-root]').forEach((root) => {
      const row = $('[data-scroller]', root);
      if (!row) return;
      const arrows = $('[data-scroller-arrows]', root);
      const prevBtn = $('[data-scroller-prev]', root);
      const nextBtn = $('[data-scroller-next]', root);
      const dotsWrap = $('[data-scroller-dots]', root);
      let pages = 1;
      const behavior = () => (reduceMotion() ? 'auto' : 'smooth');
      const step = (dir) => {
        const first = row.firstElementChild;
        const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
        const card = first ? first.getBoundingClientRect().width + gap : row.clientWidth;
        const perView = Math.max(1, Math.floor((row.clientWidth + gap) / card));
        row.scrollBy({ left: dir * card * perView, behavior: behavior() });
      };
      const measure = () => {
        const max = row.scrollWidth - row.clientWidth;
        const newPages = max <= 2 ? 1 : Math.ceil(max / row.clientWidth - 0.05) + 1;
        const page = max <= 2 ? 0 : Math.round((row.scrollLeft / max) * (newPages - 1));
        if (arrows) arrows.hidden = newPages <= 1;
        if (prevBtn) prevBtn.disabled = row.scrollLeft <= 2;
        if (nextBtn) nextBtn.disabled = row.scrollLeft >= max - 2;
        if (dotsWrap) {
          if (newPages !== pages || !dotsWrap.childElementCount) {
            pages = newPages;
            dotsWrap.innerHTML = pages <= 1 ? '' : `<div class="flex items-center justify-center">${Array.from({ length: pages }, (_, i) => `
              <button type="button" class="group inline-flex h-11 w-7 items-center justify-center" aria-label="${dotsWrap.dataset.label || 'Show page'} ${i + 1}" data-page="${i}">
                <span aria-hidden="true" class="block h-[7px] w-[7px] rounded-full bg-navy/25 transition-all duration-300 group-hover:bg-navy/50 group-aria-[current=true]:w-5 group-aria-[current=true]:bg-navy"></span>
              </button>`).join('')}</div>`;
          }
          $$('[data-page]', dotsWrap).forEach((d, i) => d.setAttribute('aria-current', String(i === page)));
        }
        pages = newPages;
      };
      dotsWrap?.addEventListener('click', (e) => {
        const d = e.target.closest('[data-page]');
        if (!d) return;
        const max = row.scrollWidth - row.clientWidth;
        row.scrollTo({ left: pages <= 1 ? 0 : (max * Number(d.dataset.page)) / (pages - 1), behavior: behavior() });
      });
      prevBtn?.addEventListener('click', () => step(-1));
      nextBtn?.addEventListener('click', () => step(1));
      if (row.hasAttribute('data-scroller-keys')) {
        row.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
        });
      }
      let raf;
      row.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); }, { passive: true });
      new ResizeObserver(measure).observe(row);
      measure();
    });
  }

  /* ---------- Product gallery + lightbox ---------- */
  const Gallery = (() => {
    let root;
    let slides = [];
    let index = 0;
    const lightboxImages = () => $$('[data-lightbox-image]');
    function select(i) {
      if (!root || !slides.length) return;
      index = (i + slides.length) % slides.length;
      $('[data-gallery-track]', root).style.transform = `translate3d(-${index * 100}%,0,0)`;
      slides.forEach((s, n) => {
        s.toggleAttribute('aria-hidden', n !== index);
        const b = $('button', s);
        if (b) b.tabIndex = n === index ? 0 : -1;
      });
      $$('[data-thumb]', root).forEach((t) => t.setAttribute('aria-current', String(Number(t.dataset.thumb) === index)));
      const counter = $('[data-gallery-counter]', root);
      if (counter) counter.textContent = index + 1;
      const status = $('[data-gallery-status]', root);
      if (status) status.textContent = `Image ${index + 1} of ${slides.length}`;
      lightboxImages().forEach((img) => { img.hidden = Number(img.dataset.lightboxImage) !== index; });
      const lc = $('[data-lightbox-counter]');
      if (lc) lc.textContent = index + 1;
    }
    function init() {
      root = $('[data-gallery-root]');
      if (!root) return;
      slides = $$('[data-gallery-slide]', root);
      const viewer = $('[data-gallery-viewer]', root);
      $$('[data-thumb]', root).forEach((t) => t.addEventListener('click', () => select(Number(t.dataset.thumb))));
      $('[data-gallery-next]', root)?.addEventListener('click', () => select(index + 1));
      $('[data-gallery-prev]', root)?.addEventListener('click', () => select(index - 1));
      viewer.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); select(index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); select(index - 1); }
      });
      addSwipe(viewer, () => select(index + 1), () => select(index - 1));
      // hover zoom (fine pointers only)
      const canZoom = () => mq('(hover: hover) and (pointer: fine)') && !reduceMotion();
      $$('[data-gallery-open]', root).forEach((btn) => {
        const layer = $('[data-zoom-layer]', btn);
        btn.addEventListener('mousemove', (e) => {
          if (!canZoom()) return;
          const r = btn.getBoundingClientRect();
          layer.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
          layer.style.transform = 'scale(1.6)';
        });
        btn.addEventListener('mouseleave', () => { layer.style.transform = ''; });
        btn.addEventListener('click', () => { layer.style.transform = ''; select(index); Dialogs.open('lightbox', btn); });
      });
      const lb = $('[data-lightbox]');
      if (lb) {
        $('[data-lightbox-next]', lb)?.addEventListener('click', () => select(index + 1));
        $('[data-lightbox-prev]', lb)?.addEventListener('click', () => select(index - 1));
        addSwipe(lb, () => select(index + 1), () => select(index - 1));
        document.addEventListener('keydown', (e) => {
          if (!Dialogs.isOpen('lightbox')) return;
          if (e.key === 'ArrowRight') select(index + 1);
          if (e.key === 'ArrowLeft') select(index - 1);
        });
      }
      select(0);
    }
    return { init, select };
  })();

  /* ---------- Money ---------- */
  function formatMoney(value) {
    if (typeof value === 'string' && /[^\d.]/.test(value)) return value;
    const cents = typeof value === 'string' ? Math.round(parseFloat(value) * 100) : value;
    const fmt = config.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const noDec = Math.round(cents / 100).toLocaleString('en-US');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => (k === 'amount_no_decimals' ? noDec : k === 'amount_with_comma_separator' ? amount.replace('.', ',') : Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })));
  }
  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

  /* ---------- Cart (AJAX API + Section Rendering) ---------- */
  const Cart = (() => {
    async function refresh() {
      const res = await fetch(`${config.routes.root || '/'}?sections=kaira-cart-drawer`);
      const data = await res.json();
      const html = data['kaira-cart-drawer'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-drawer-inner]', doc);
      const current = $('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      const title = $('#cart-drawer-title', doc);
      if (title) $('#cart-drawer-title').textContent = title.textContent;
      updateCount(Number(fresh?.dataset.cartCountValue || 0));
    }
    function updateCount(count) {
      $$('[data-cart-count]').forEach((b) => { b.textContent = count; b.classList.toggle('hidden', count === 0); });
      $$('[data-cart-count-text]').forEach((b) => { b.textContent = count; });
      $$('[data-cart-button]').forEach((b) => b.setAttribute('aria-label', `Shopping bag, ${count} ${count === 1 ? 'item' : 'items'}`));
    }
    async function add(formData) {
      const res = await fetch(config.routes.cartAdd || '/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag');
      await refresh();
      return data;
    }
    async function change(line, quantity) {
      const res = await fetch(config.routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); announce(d.description || 'That quantity is not available.'); }
      await refresh();
      announce(quantity === 0 ? 'Item removed from your bag.' : `Quantity updated to ${quantity}.`);
      $('[data-dialog="cart-drawer"] [data-dialog-panel]')?.focus();
    }
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-cart-qty]');
      if (!b || b.disabled) return;
      b.disabled = true;
      change(Number(b.dataset.line), Number(b.dataset.cartQty));
    });
    return { add, refresh };
  })();

  /* ---------- Product form ---------- */
  function initProduct() {
    const section = $('[data-product-section]');
    if (!section) return;
    const form = $('#KairaProductForm');
    const json = $('[data-product-json]', section);
    const data = json ? JSON.parse(json.textContent) : { variants: [] };
    const idInput = $('[data-variant-id]', form);
    const qtyInput = $('[data-qty-input]', form);
    const minus = $('[data-qty-step="-1"]', form);
    const plus = $('[data-qty-step="1"]', form);
    const atc = $('[data-add-to-cart]', form);
    const atcLabel = $('[data-atc-label]', form);
    const buyNow = $('[data-buy-now]', form);
    const error = $('[data-form-error]', form);
    const sticky = $('[data-sticky-bar]', section);
    const stickyAdd = $('[data-sticky-add]', section);
    let variant = data.variants.find((v) => String(v.id) === idInput.value) || data.variants[0];

    const maxQty = () => (variant && variant.tracked && variant.deny ? Math.max(1, variant.qty) : 99);
    const syncQty = () => {
      const q = Math.min(Math.max(1, Number(qtyInput.value) || 1), maxQty());
      qtyInput.value = q;
      qtyInput.max = maxQty();
      minus.disabled = q <= 1;
      plus.disabled = q >= maxQty();
      if (variant?.available) atcLabel.textContent = `Add to Cart · ${variant.price}`;
    };
    $$('[data-qty-step]', form).forEach((b) => b.addEventListener('click', () => { qtyInput.value = Number(qtyInput.value) + Number(b.dataset.qtyStep); syncQty(); }));

    // variant selection
    $$('[data-option-input]', form).forEach((input) => input.addEventListener('change', () => {
      const selected = $$('fieldset', form).map((fs) => $('[data-option-input]:checked', fs)?.value);
      const match = data.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      $$('[data-option-label]', form).forEach((l) => { l.textContent = selected[Number(l.dataset.optionLabel)] || ''; });
      if (!match) { atc.disabled = true; if (buyNow) buyNow.disabled = true; atcLabel.textContent = 'Unavailable'; return; }
      variant = match;
      idInput.value = match.id;
      $$('[data-price]', section).forEach((p) => { p.textContent = match.price; });
      atc.disabled = !match.available; if (buyNow) buyNow.disabled = !match.available;
      if (stickyAdd) stickyAdd.disabled = !match.available;
      atcLabel.textContent = match.available ? `Add to Cart · ${match.price}` : 'Sold Out';
      const stock = $('[data-stock]', section);
      if (stock) {
        stock.innerHTML = !match.available
          ? '<span class="h-2.5 w-2.5 rounded-full bg-brick" aria-hidden="true"></span><span><strong class="font-medium">Sold out</strong></span>'
          : match.tracked && match.qty <= data.lowStock
            ? `<span class="h-2.5 w-2.5 rounded-full bg-saffron" aria-hidden="true"></span><span><strong class="font-medium">Low stock:</strong> Only ${match.qty} left in stock</span>`
            : '<span class="h-2.5 w-2.5 rounded-full bg-turquoise" aria-hidden="true"></span><span><strong class="font-medium">In stock</strong></span>';
      }
      const url = new URL(location.href);
      url.searchParams.set('variant', match.id);
      history.replaceState(null, '', url);
      syncQty();
    }));

    async function submit({ checkout = false } = {}) {
      error.classList.add('hidden');
      atc.disabled = true; if (buyNow) buyNow.disabled = true;
      try {
        await Cart.add(new FormData(form));
        announce(`${document.querySelector('h1')?.textContent.trim() || 'Item'} added to your bag.`);
        if (checkout) { location.href = '/checkout'; return; }
        Dialogs.open('cart-drawer', atc);
      } catch (err) {
        error.textContent = err.message;
        error.classList.remove('hidden');
      } finally {
        atc.disabled = !variant?.available; if (buyNow) buyNow.disabled = !variant?.available;
      }
    }
    form.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
    buyNow?.addEventListener('click', () => submit({ checkout: true }));
    stickyAdd?.addEventListener('click', () => submit());
    syncQty();

    // sticky mobile bar: after the main buttons scroll away, hidden near the trust strip / footer
    if (sticky) {
      let pastAtc = false;
      let nearEnd = false;
      const update = () => {
        const show = pastAtc && !nearEnd;
        sticky.classList.toggle('is-visible', show);
        sticky.inert = !show;
        sticky.setAttribute('aria-hidden', String(!show));
      };
      new IntersectionObserver(([e]) => { pastAtc = !e.isIntersecting && e.boundingClientRect.top < 0; update(); }).observe($('[data-atc-anchor]', form));
      const end = $('#trust-strip') || $('#site-footer');
      if (end) new IntersectionObserver(([e]) => { nearEnd = e.isIntersecting || e.boundingClientRect.top < 0; update(); }).observe(end);
    }
  }

  /* ---------- Size guide units ---------- */
  function initSizeGuide() {
    const root = $('[data-size-guide]');
    if (!root) return;
    $$('[data-unit]', root).forEach((b) => b.addEventListener('click', () => {
      const unit = b.dataset.unit;
      $$('[data-unit]', root).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      $$('[data-inches]', root).forEach((td) => {
        const inches = Number(td.dataset.inches);
        td.textContent = unit === 'in' ? `${inches}”` : `${Math.round(inches * 2.54)} cm`;
      });
    }));
  }

  /* ---------- Wishlist (localStorage) ---------- */
  const Wishlist = (() => {
    const KEY = 'namai.wishlist';
    const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };
    const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* storage unavailable */ } };
    function sync() {
      const list = read();
      $$('[data-wishlist-count]').forEach((b) => { b.textContent = list.length; b.classList.toggle('is-visible', list.length > 0); });
      $$('[data-wishlist-button]').forEach((b) => b.setAttribute('aria-label', `Wishlist, ${list.length} saved`));
      $$('[data-wishlist-toggle]').forEach((b) => {
        const on = list.includes(b.dataset.wishlistToggle);
        b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
      });
    }
    async function renderDrawer() {
      const list = read();
      const empty = $('[data-wishlist-empty]');
      const ul = $('[data-wishlist-list]');
      if (!ul) return;
      empty.hidden = list.length > 0;
      empty.classList.toggle('hidden', list.length > 0);
      ul.classList.toggle('hidden', list.length === 0);
      if (!list.length) { ul.innerHTML = ''; return; }
      const products = (await Promise.all(list.map((h) => fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)))).filter(Boolean);
      ul.innerHTML = products.map((p) => `
        <li class="flex gap-4 py-5">
          <a href="${p.url}" class="w-[96px] shrink-0"><div class="relative overflow-hidden" style="aspect-ratio:3/4;background:var(--soft-gray)">${p.featured_image ? `<img src="${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=200" alt="" class="absolute inset-0 h-full w-full object-cover" loading="lazy">` : ''}</div></a>
          <div class="min-w-0 flex-1">
            <a href="${p.url}" class="font-serif text-[18px] leading-snug text-navy">${escapeHtml(p.title)}</a>
            <p class="mt-1 text-[14px] text-navy">${formatMoney(p.price)}</p>
            <div class="mt-3 grid gap-2">
              ${p.available ? `<button type="button" class="btn-primary w-full" data-wishlist-add="${p.variants[0].id}">Move to Bag</button>` : '<p class="text-[13px] text-muted">Sold out</p>'}
              <button type="button" class="btn-secondary w-full" data-wishlist-remove="${p.handle}">Remove</button>
            </div>
          </div>
        </li>`).join('');
    }
    document.addEventListener('click', async (e) => {
      const t = e.target.closest('[data-wishlist-toggle]');
      if (t) {
        const h = t.dataset.wishlistToggle;
        const list = read();
        const has = list.includes(h);
        write(has ? list.filter((x) => x !== h) : [...list, h]);
        announce(has ? 'Removed from your wishlist.' : 'Saved to your wishlist.');
        sync();
        return;
      }
      const r = e.target.closest('[data-wishlist-remove]');
      if (r) { write(read().filter((x) => x !== r.dataset.wishlistRemove)); sync(); renderDrawer(); announce('Removed from your wishlist.'); return; }
      const a = e.target.closest('[data-wishlist-add]');
      if (a) {
        const fd = new FormData();
        fd.append('id', a.dataset.wishlistAdd);
        fd.append('quantity', '1');
        try { await Cart.add(fd); Dialogs.close($('[data-dialog="wishlist-drawer"]'), { restoreFocus: false }); Dialogs.open('cart-drawer'); announce('Added to your bag.'); }
        catch (err) { announce(err.message); }
      }
    });
    document.addEventListener('dialog:open', (e) => { if (e.target.dataset.dialog === 'wishlist-drawer') renderDrawer(); });
    return { sync };
  })();

  /* ---------- Reveal on scroll (one time) ---------- */
  function initReveal() {
    const items = $$('.reveal');
    if (!('IntersectionObserver' in window) || reduceMotion()) { items.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    items.forEach((el) => io.observe(el));
  }

  /* ---------- Product cards: flip through photos on hover / swipe ---------- */
  function initCards(root = document) {
    $$('[data-card-media]', root).forEach((media) => {
      if (media.dataset.cardReady) return;
      media.dataset.cardReady = '1';
      const imgs = $$('.card-img', media);
      const bars = $$('[data-card-bar]', media);
      if (imgs.length < 2) return;
      let current = 0;
      const show = (i) => {
        if (i === current) return;
        imgs[current].classList.replace('opacity-100', 'opacity-0');
        bars[current]?.classList.replace('bg-white', 'bg-white/45');
        current = i;
        imgs[current].classList.replace('opacity-0', 'opacity-100');
        bars[current]?.classList.replace('bg-white/45', 'bg-white');
      };
      const warm = () => imgs.forEach((img) => { if (img.loading === 'lazy') img.loading = 'eager'; });
      media.addEventListener('mouseenter', warm, { once: true });
      media.addEventListener('mousemove', (e) => {
        const r = media.getBoundingClientRect();
        show(Math.min(imgs.length - 1, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * imgs.length))));
      });
      media.addEventListener('mouseleave', () => show(0));
      // touch: swipe sideways to change photo; a tap still opens the product
      let start = null;
      let swiped = false;
      media.addEventListener('touchstart', (e) => { warm(); media.classList.add('is-touched'); const t = e.touches[0]; start = { x: t.clientX, y: t.clientY }; swiped = false; }, { passive: true });
      media.addEventListener('touchend', (e) => {
        if (!start) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        start = null;
        if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
          swiped = true;
          show((current + (dx < 0 ? 1 : -1) + imgs.length) % imgs.length);
        }
      }, { passive: true });
      media.addEventListener('click', (e) => { if (swiped) { e.preventDefault(); swiped = false; } });
    });
  }

  /* ---------- Dropdowns built on <details> (category buttons, filters) ---------- */
  function initDropdowns() {
    document.addEventListener('click', (e) => {
      $$('details[data-dropdown][open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      $$('details[data-dropdown][open]').forEach((d) => { d.removeAttribute('open'); $('summary', d)?.focus(); });
    });
    document.addEventListener('toggle', (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open || !d.hasAttribute('data-dropdown')) return;
      $$('details[data-dropdown][open]').forEach((o) => { if (o !== d) o.removeAttribute('open'); });
    }, true);
  }

  /* ---------- Collection: load more ---------- */
  function initLoadMore() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-load-more]');
      if (!btn) return;
      e.preventDefault();
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Loading…';
      try {
        const html = await (await fetch(btn.href)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = $('[data-collection-grid]');
        $$('[data-collection-grid] > li', doc).forEach((li) => grid.appendChild(document.importNode(li, true)));
        const wrap = $('[data-load-more-wrap]');
        const fresh = $('[data-load-more-wrap]', doc);
        if (fresh) wrap.replaceWith(document.importNode(fresh, true)); else wrap.remove();
        history.replaceState(null, '', btn.href);
        initCards(grid);
        announce('More products loaded.');
      } catch {
        location.href = btn.href;
      }
    });
  }

  /* ---------- Product recommendations + recently viewed ---------- */
  function initRecommendations() {
    const root = $('[data-recommendations]');
    const handle = root?.dataset.productHandle || $('[data-product-json]') && JSON.parse($('[data-product-json]').textContent).handle;
    // remember this product
    if (handle) {
      try {
        const list = JSON.parse(localStorage.getItem('kaira.recent') || '[]').filter((h) => h !== handle);
        localStorage.setItem('kaira.recent', JSON.stringify([handle, ...list].slice(0, 12)));
      } catch { /* storage unavailable */ }
    }
    if (!root) return;
    const tabs = $$('[data-tab]', root);
    tabs.forEach((t) => t.addEventListener('click', () => {
      tabs.forEach((x) => x.setAttribute('aria-selected', String(x === t)));
      $$('[data-tab-panel]', root).forEach((p) => { p.hidden = p.dataset.tabPanel !== t.dataset.tab; });
      if (t.dataset.tab === 'recent') loadRecent();
    }));
    // recommendations come from Shopify after page load (Section Rendering API)
    fetch(root.dataset.url).then((r) => r.text()).then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-rec-grid]', doc);
      const grid = $('[data-rec-grid]', root);
      if (fresh && fresh.children.length) { grid.innerHTML = fresh.innerHTML; initCards(grid); }
      else if (!grid.children.length) { tabs[1]?.click(); }
    }).catch(() => {});
    let loaded = false;
    async function loadRecent() {
      if (loaded) return;
      loaded = true;
      let list = [];
      try { list = JSON.parse(localStorage.getItem('kaira.recent') || '[]').filter((h) => h !== handle).slice(0, 5); } catch { /* */ }
      const grid = $('[data-recent-grid]', root);
      const empty = $('[data-recent-empty]', root);
      if (!list.length) { empty.classList.remove('hidden'); return; }
      const products = (await Promise.all(list.map((h) => fetch(`/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)))).filter(Boolean);
      grid.innerHTML = products.map((p) => {
        const imgs = (p.images || []).slice(0, 6);
        const src = (u, w) => `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
        return `<li><div class="product-card group" data-product-card>
          <a href="${p.url}" class="relative block overflow-hidden bg-soft-gray" style="aspect-ratio:3/4" aria-label="${escapeHtml(p.title)}" ${imgs.length > 1 ? 'data-card-media' : ''}>
            ${imgs.map((u, i) => `<img src="${src(u, 600)}" alt="" loading="lazy" class="card-img absolute inset-0 h-full w-full object-cover ${i ? 'opacity-0' : 'opacity-100'}">`).join('')}
            ${p.available ? '' : '<span class="absolute left-2.5 top-2.5 z-[1] bg-badge px-2 py-1 text-[9.5px] font-medium uppercase leading-none tracking-[0.06em] text-badge-text">Sold out</span>'}
            ${imgs.length > 1 ? `<span class="pointer-events-none absolute inset-x-3 bottom-2.5 z-[1] flex gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100" aria-hidden="true">${imgs.map((_, i) => `<span class="card-bar h-[2px] flex-1 ${i ? 'bg-white/45' : 'bg-white'}" data-card-bar></span>`).join('')}</span>` : ''}
          </a>
          <div class="mt-3"><a href="${p.url}" class="block text-[12.5px] font-medium leading-snug text-navy">${escapeHtml(p.title)}</a><p class="mt-2 text-[12.5px] text-navy">${formatMoney(p.price)}</p></div>
        </div></li>`;
      }).join('');
      initCards(grid);
    }
  }

  /* ---------- Boot ---------- */
  function boot() {
    initPopovers();
    initAnnouncement();
    initMobileHeader();
    DesktopSearch.init();
    initMega();
    initSearch();
    initHero();
    initScrollers();
    Gallery.init();
    initProduct();
    initSizeGuide();
    Wishlist.sync();
    initCards();
    initDropdowns();
    initLoadMore();
    initRecommendations();
    initReveal();
    if (location.hash) {
      const id = location.hash.slice(1);
      if (document.getElementById(id)) setTimeout(() => document.getElementById(id).scrollIntoView(), 50);
    }
  }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();

  // Theme editor: re-initialise per-section widgets when a section is re-rendered
  document.addEventListener('shopify:section:load', () => { initHero(); initScrollers(); Gallery.init(); initSizeGuide(); initReveal(); });
})();
